
import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Notification } from '../types';
import NotificationBadge from './NotificationBadge';
import NotificationList from './NotificationList';

interface NotificationCenterProps {
    userId: string;
    notifications: Notification[];
    onRefresh?: () => void;
    mobileView?: boolean;
}

const NotificationCenter: React.FC<NotificationCenterProps> = ({
    userId,
    notifications,
    onRefresh,
    mobileView = false,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    const unreadCount = notifications.filter(n => !n.isRead).length;
    const highestPriority = notifications
        .filter(n => !n.isRead)
        .reduce((highest, n) => {
            const priorities = { LOW: 1, MEDIUM: 2, HIGH: 3, CRITICAL: 4 };
            const currentPriority = priorities[n.priority];
            const highestPriority = priorities[highest];
            return currentPriority > highestPriority ? n.priority : highest;
        }, 'LOW' as const);

    return (
        <div ref={dropdownRef} className="relative">
            {/* Bell Icon Button */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="relative p-2 rounded-full hover:bg-gray-100 transition-all font-sans"
                aria-label="Bildirimler"
            >
                <span className="text-2xl">🔔</span>

                {/* Badge */}
                {unreadCount > 0 && (
                    <div className="absolute -top-1 -right-1">
                        <NotificationBadge
                            count={unreadCount}
                            priority={highestPriority}
                            showPulse={true}
                            size="sm"
                        />
                    </div>
                )}
            </button>

            {/* Dropdown Panel */}
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        transition={{ duration: 0.3, ease: [0.32, 0.72, 0, 1] }}
                        initial={{ opacity: 0, y: 10, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        className="absolute right-0 mt-3 w-96 max-h-[80vh] bg-white rounded-[2rem] shadow-2xl border border-gray-100 overflow-hidden z-[1000] ring-1 ring-black/5 flex flex-col"
                    >
                        <NotificationList
                            userId={userId}
                            notifications={notifications}
                            onRefresh={onRefresh}
                            onClose={() => setIsOpen(false)}
                        />

                        {/* Footer */}
                        <div className="p-4 border-t border-gray-100 bg-gray-50/50">
                            <button
                                onClick={() => {
                                    setIsOpen(false);
                                    // Navigate to full page if needed
                                }}
                                className="w-full py-3 bg-white border border-gray-100 rounded-xl text-[10px] font-black uppercase tracking-widest text-brand-clay hover:bg-gray-50 transition-all shadow-sm"
                            >
                                Tümünü Görüntüle →
                            </button>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div >
    );
};

export default NotificationCenter;

